import { SequenceHelper } from "@swivl-mono/helpers";
import { FMSType, LocationVarKey, SequenceEnabledStatus, SequenceInterface, SequenceSubType } from "@swivl-mono/types";
import { OptionsHelper } from "@swivl-mono/ui";
import { useEffect, useMemo } from "react";

import { IF_ALL, IS_EQUAL, IS_NOT_EQUAL } from "../../../../Components/Worksheets/Types/Worksheet.Interface";
import {
  DropdownRow,
  PageHeader,
  Section,
  SectionHeader,
  SwitchRow,
  TextAreaRow
} from "../../../../Components/Worksheets/WorksheetComponent/WorksheetComponents";
import { usePropertiesGetLocationIds } from "../../../../Hooks/usePropertiesGetLocationIds";
import SequencesModel from "../../../../Models/Sequences/SequencesModel";

export const PAGE_KEYS: LocationVarKey[] = [
  LocationVarKey.WantsCronReceiptsMoveOut,
  LocationVarKey.HasFMSPlusAbandonedCartNotes,
  LocationVarKey.SequenceMoveOutDay00Enabled,
  LocationVarKey.SequenceMoveOutDay00MinTime,
  LocationVarKey.SequenceMoveOutDay00MaxTime,
  LocationVarKey.FMSplushas0MoveOutMethod,
  LocationVarKey.FMSplus0MoveOut1,
  LocationVarKey.FMSplus0MoveOut2,
  LocationVarKey.FMSplus0MoveOut3,
  LocationVarKey.FMSplus0MoveOutReview,
  LocationVarKey.SequenceMoveOutDay01Enabled,
  LocationVarKey.SequenceMoveOutDay01Time,
  LocationVarKey.FMSplushasp1MoveOutMethod,
  LocationVarKey.FMSplusp1MoveOut1,
  LocationVarKey.FMSplusp1MoveOut2,
  LocationVarKey.FMSplusp1MoveOut3,
  LocationVarKey.FMSplushasp1MoveOutReview,
  LocationVarKey.SequenceMoveOutDay02Enabled,
  LocationVarKey.SequenceMoveOutDay02Time,
  LocationVarKey.SequenceMoveOutDay03Enabled,
  LocationVarKey.SequenceMoveOutDay03Time,
  LocationVarKey.FMSplushasp3MoveOutMethod,
  LocationVarKey.FMSplusp3MoveOut1,
  LocationVarKey.FMSplusp3MoveOut2,
  LocationVarKey.FMSplusp3MoveOut3,
  LocationVarKey.FMSplushasp3MoveOutReview,
  LocationVarKey.SequenceMoveOutDay05Enabled,
  LocationVarKey.SequenceMoveOutDay05Time,
  LocationVarKey.FMSplushasp5MoveOutMethod,
  LocationVarKey.FMSplusp5MoveOut1,
  LocationVarKey.FMSplusp5MoveOut2,
  LocationVarKey.FMSplusp5MoveOut3,
  LocationVarKey.FMSplushasp5MoveOutReview,
  LocationVarKey.SequenceMoveOutDay07Enabled,
  LocationVarKey.SequenceMoveOutDay07Time,
  LocationVarKey.FMSplushasp7MoveOutMethod,
  LocationVarKey.FMSplusp7MoveOut1,
  LocationVarKey.FMSplusp7MoveOut2,
  LocationVarKey.FMSplusp7MoveOut3,
  LocationVarKey.FMSplushasp7MoveOutReview,
  LocationVarKey.SequenceMoveOutDay10Enabled,
  LocationVarKey.SequenceMoveOutDay10Time,
  LocationVarKey.FMSplushasp10MoveOutMethod,
  LocationVarKey.FMSplusp10MoveOut1,
  LocationVarKey.FMSplusp10MoveOut2,
  LocationVarKey.FMSplusp10MoveOut3,
  LocationVarKey.FMSplushasp10MoveOutReview,
  LocationVarKey.SequenceMoveOutDay14Enabled,
  LocationVarKey.SequenceMoveOutDay14Time,
  LocationVarKey.FMSplushasp14MoveOutMethod,
  LocationVarKey.FMSplusp14MoveOut1,
  LocationVarKey.FMSplusp14MoveOut2,
  LocationVarKey.FMSplusp14MoveOut3,
  LocationVarKey.FMSplushasp14MoveOutReview,
  LocationVarKey.SequenceMoveOutDay30Enabled,
  LocationVarKey.SequenceMoveOutDay30Time,
  LocationVarKey.FMSplushasp30MoveOutMethod,
  LocationVarKey.FMSplusp30MoveOut1,
  LocationVarKey.FMSplusp30MoveOut2,
  LocationVarKey.FMSplusp30MoveOut3,
  LocationVarKey.FMSplushasp30MoveOutReview
];

export default function WorksheetView() {
  const { sequences } = SequencesModel.useState();
  const page = usePropertiesGetLocationIds();
  useEffect(() => {
    SequencesModel.load(false);
  }, []);
  const sequenceArray: SequenceInterface[] | undefined = useMemo(() => {
    if (sequences) {
      return Object.values(sequences);
    }
    return undefined;
  }, [sequences]);

  return (
    <>
      <PageHeader
        {...page}
        subheading="These sync with your FMS daily and will trigger a workflow to communicate with your customers around move out."
      >
        Move Out Sequences
      </PageHeader>
      <SwitchRow {...page} keyName={LocationVarKey.WantsCronReceiptsMoveOut} label="Send Email Receipts" />
      <SwitchRow
        {...page}
        hideIf={IF_ALL([
          IS_NOT_EQUAL(LocationVarKey.FMSType, FMSType.SiteLink),
          IS_NOT_EQUAL(LocationVarKey.FMSType, FMSType.StorEdge),
          IS_NOT_EQUAL(LocationVarKey.FMSType, FMSType.SSMCloud),
          IS_NOT_EQUAL(LocationVarKey.FMSType, FMSType.QuikStor),
          IS_NOT_EQUAL(LocationVarKey.FMSType, FMSType.Monument),
          IS_NOT_EQUAL(LocationVarKey.FMSType, FMSType.Yardi)
        ])}
        keyName={LocationVarKey.HasFMSPlusAbandonedCartNotes}
        label="Add FMS Notes"
      />

      {sequenceArray && SequenceHelper.isAnySequenceSubType(SequenceSubType.MOVE_OUT_DAY_00, sequenceArray) && (
        <>
          <SectionHeader {...page}>Day of move out</SectionHeader>
          <DropdownRow
            {...page}
            label="Enabled"
            marginTop
            keyName={LocationVarKey.SequenceMoveOutDay00Enabled}
            options={[
              { label: "Enabled", value: SequenceEnabledStatus.ENABLED },
              { label: "Disabled", value: SequenceEnabledStatus.DISABLED },
              { label: "Simulated", value: SequenceEnabledStatus.SIMULATED }
            ]}
          />
          <Section {...page} hideIf={IS_EQUAL(LocationVarKey.SequenceMoveOutDay00Enabled, SequenceEnabledStatus.DISABLED)}>
            <DropdownRow
              {...page}
              label="Send Between Start"
              marginTop
              keyName={LocationVarKey.SequenceMoveOutDay00MinTime}
              options={OptionsHelper.hoursOptionsLocalized()}
            />
            <DropdownRow
              {...page}
              label="Send Between End"
              marginTop
              keyName={LocationVarKey.SequenceMoveOutDay00MaxTime}
              options={OptionsHelper.hoursOptionsLocalized()}
            />
            <DropdownRow
              {...page}
              marginTop
              label=" Channel"
              keyName={LocationVarKey.FMSplushas0MoveOutMethod}
              options={[
                {
                  label: "Email",
                  value: "Email"
                },
                {
                  label: "SMS",
                  value: "SMS"
                },
                {
                  label: "Email & SMS",
                  value: "Both"
                }
              ]}
            />
            <Section {...page} hideIf={IS_EQUAL(LocationVarKey.FMSplushas0MoveOutMethod, "Email")}>
              <TextAreaRow {...page} marginTop label=" SMS Message 1" keyName={LocationVarKey.FMSplus0MoveOut1} />
              <TextAreaRow
                {...page}
                marginTop
                label=" SMS Message 2 (Optional)"
                keyName={LocationVarKey.FMSplus0MoveOut2}
                enableSwitchVar={LocationVarKey.FMSplus0MoveOut2Enabled}
              />
              <TextAreaRow
                {...page}
                marginTop
                label=" SMS Message 3 (Optional)"
                keyName={LocationVarKey.FMSplus0MoveOut3}
                enableSwitchVar={LocationVarKey.FMSplus0MoveOut3Enabled}
              />
            </Section>
            <SwitchRow {...page} keyName={LocationVarKey.FMSplus0MoveOutReview} label="Start Review Solicitation" />
          </Section>
        </>
      )}

      {sequenceArray && SequenceHelper.isAnySequenceSubType(SequenceSubType.MOVE_OUT_DAY_01, sequenceArray) && (
        <>
          <SectionHeader {...page}>1 day after move out</SectionHeader>
          <DropdownRow
            {...page}
            label="Enabled"
            marginTop
            keyName={LocationVarKey.SequenceMoveOutDay01Enabled}
            options={[
              { label: "Enabled", value: SequenceEnabledStatus.ENABLED },
              { label: "Disabled", value: SequenceEnabledStatus.DISABLED },
              { label: "Simulated", value: SequenceEnabledStatus.SIMULATED }
            ]}
          />
          <Section {...page} hideIf={IS_EQUAL(LocationVarKey.SequenceMoveOutDay01Enabled, SequenceEnabledStatus.DISABLED)}>
            <DropdownRow
              {...page}
              label="Time"
              marginTop
              keyName={LocationVarKey.SequenceMoveOutDay01Time}
              options={OptionsHelper.hoursOptionsLocalized()}
            />
            <DropdownRow
              {...page}
              marginTop
              label=" Channel"
              keyName={LocationVarKey.FMSplushasp1MoveOutMethod}
              options={[
                {
                  label: "Email",
                  value: "Email"
                },
                {
                  label: "SMS",
                  value: "SMS"
                },
                {
                  label: "Email & SMS",
                  value: "Both"
                }
              ]}
            />
            <Section {...page} hideIf={IS_EQUAL(LocationVarKey.FMSplushasp1MoveOutMethod, "Email")}>
              <TextAreaRow {...page} marginTop label=" SMS Message 1" keyName={LocationVarKey.FMSplusp1MoveOut1} />
              <TextAreaRow
                {...page}
                marginTop
                label=" SMS Message 2 (Optional)"
                keyName={LocationVarKey.FMSplusp1MoveOut2}
                enableSwitchVar={LocationVarKey.FMSplusp1MoveOut2Enabled}
              />
              <TextAreaRow
                {...page}
                marginTop
                label=" SMS Message 3 (Optional)"
                keyName={LocationVarKey.FMSplusp1MoveOut3}
                enableSwitchVar={LocationVarKey.FMSplusp1MoveOut3Enabled}
              />
            </Section>
            <SwitchRow {...page} keyName={LocationVarKey.FMSplushasp1MoveOutReview} label="Start Review Solicitation" />
          </Section>
        </>
      )}

      {/* {sequenceArray && SequenceHelper.isAnySequenceSubType(SequenceSubType.MOVE_OUT_DAY_02, sequenceArray) && (
        <>
          <SectionHeader {...page}>2 days after move out</SectionHeader>
          <DropdownRow
            {...page}
            label="Enabled"
            marginTop
            keyName={LocationVarKey.SequenceMoveOutDay02Enabled}
            options={[
              { label: "Enabled", value: SequenceEnabledStatus.ENABLED },
              { label: "Disabled", value: SequenceEnabledStatus.DISABLED },
              { label: "Simulated", value: SequenceEnabledStatus.SIMULATED },
            ]}
          />
          <Section {...page} hideIf={IS_EQUAL(LocationVarKey.SequenceMoveOutDay02Enabled, SequenceEnabledStatus.DISABLED)}>
            <DropdownRow
              {...page}
              label="Time"
              marginTop
              keyName={LocationVarKey.SequenceMoveOutDay02Time}
              options={OptionsHelper.hoursOptionsLocalized()}
            />
          </Section>
        </>
      )} */}

      {sequenceArray && SequenceHelper.isAnySequenceSubType(SequenceSubType.MOVE_OUT_DAY_03, sequenceArray) && (
        <>
          <SectionHeader {...page}>3 days after move out</SectionHeader>
          <DropdownRow
            {...page}
            label="Enabled"
            marginTop
            keyName={LocationVarKey.SequenceMoveOutDay03Enabled}
            options={[
              { label: "Enabled", value: SequenceEnabledStatus.ENABLED },
              { label: "Disabled", value: SequenceEnabledStatus.DISABLED },
              { label: "Simulated", value: SequenceEnabledStatus.SIMULATED }
            ]}
          />
          <Section {...page} hideIf={IS_EQUAL(LocationVarKey.SequenceMoveOutDay03Enabled, SequenceEnabledStatus.DISABLED)}>
            <DropdownRow
              {...page}
              label="Time"
              marginTop
              keyName={LocationVarKey.SequenceMoveOutDay03Time}
              options={OptionsHelper.hoursOptionsLocalized()}
            />
            <DropdownRow
              {...page}
              marginTop
              label=" Channel"
              keyName={LocationVarKey.FMSplushasp3MoveOutMethod}
              options={[
                {
                  label: "Email",
                  value: "Email"
                },
                {
                  label: "SMS",
                  value: "SMS"
                },
                {
                  label: "Email & SMS",
                  value: "Both"
                }
              ]}
            />
            <Section {...page} hideIf={IS_EQUAL(LocationVarKey.FMSplushasp3MoveOutMethod, "Email")}>
              <TextAreaRow {...page} marginTop label=" SMS Message 1" keyName={LocationVarKey.FMSplusp3MoveOut1} />
              <TextAreaRow
                {...page}
                marginTop
                label=" SMS Message 2 (Optional)"
                keyName={LocationVarKey.FMSplusp3MoveOut2}
                enableSwitchVar={LocationVarKey.FMSplusp3MoveOut2Enabled}
              />
              <TextAreaRow
                {...page}
                marginTop
                label=" SMS Message 3 (Optional)"
                keyName={LocationVarKey.FMSplusp3MoveOut3}
                enableSwitchVar={LocationVarKey.FMSplusp3MoveOut3Enabled}
              />
            </Section>
            <SwitchRow {...page} keyName={LocationVarKey.FMSplushasp3MoveOutReview} label="Start Review Solicitation" />
          </Section>
        </>
      )}

      {sequenceArray && SequenceHelper.isAnySequenceSubType(SequenceSubType.MOVE_OUT_DAY_05, sequenceArray) && (
        <>
          <SectionHeader {...page}>5 days after move out</SectionHeader>
          <DropdownRow
            {...page}
            label="Enabled"
            marginTop
            keyName={LocationVarKey.SequenceMoveOutDay05Enabled}
            options={[
              { label: "Enabled", value: SequenceEnabledStatus.ENABLED },
              { label: "Disabled", value: SequenceEnabledStatus.DISABLED },
              { label: "Simulated", value: SequenceEnabledStatus.SIMULATED }
            ]}
          />
          <Section {...page} hideIf={IS_EQUAL(LocationVarKey.SequenceMoveOutDay05Enabled, SequenceEnabledStatus.DISABLED)}>
            <DropdownRow
              {...page}
              label="Time"
              marginTop
              keyName={LocationVarKey.SequenceMoveOutDay05Time}
              options={OptionsHelper.hoursOptionsLocalized()}
            />
            <DropdownRow
              {...page}
              marginTop
              label=" Channel"
              keyName={LocationVarKey.FMSplushasp5MoveOutMethod}
              options={[
                {
                  label: "Email",
                  value: "Email"
                },
                {
                  label: "SMS",
                  value: "SMS"
                },
                {
                  label: "Email & SMS",
                  value: "Both"
                }
              ]}
            />
            <Section {...page} hideIf={IS_EQUAL(LocationVarKey.FMSplushasp5MoveOutMethod, "Email")}>
              <TextAreaRow {...page} marginTop label=" SMS Message 1" keyName={LocationVarKey.FMSplusp5MoveOut1} />
              <TextAreaRow
                {...page}
                marginTop
                label=" SMS Message 2 (Optional)"
                keyName={LocationVarKey.FMSplusp5MoveOut2}
                enableSwitchVar={LocationVarKey.FMSplusp5MoveOut2Enabled}
              />
              <TextAreaRow
                {...page}
                marginTop
                label=" SMS Message 3 (Optional)"
                keyName={LocationVarKey.FMSplusp5MoveOut3}
                enableSwitchVar={LocationVarKey.FMSplusp5MoveOut3Enabled}
              />
            </Section>
            <SwitchRow {...page} keyName={LocationVarKey.FMSplushasp5MoveOutReview} label="Start Review Solicitation" />
          </Section>
        </>
      )}

      {sequenceArray && SequenceHelper.isAnySequenceSubType(SequenceSubType.MOVE_OUT_DAY_07, sequenceArray) && (
        <>
          <SectionHeader {...page}>7 days after move out</SectionHeader>
          <DropdownRow
            {...page}
            label="Enabled"
            marginTop
            keyName={LocationVarKey.SequenceMoveOutDay07Enabled}
            options={[
              { label: "Enabled", value: SequenceEnabledStatus.ENABLED },
              { label: "Disabled", value: SequenceEnabledStatus.DISABLED },
              { label: "Simulated", value: SequenceEnabledStatus.SIMULATED }
            ]}
          />
          <Section {...page} hideIf={IS_EQUAL(LocationVarKey.SequenceMoveOutDay07Enabled, SequenceEnabledStatus.DISABLED)}>
            <DropdownRow
              {...page}
              label="Time"
              marginTop
              keyName={LocationVarKey.SequenceMoveOutDay07Time}
              options={OptionsHelper.hoursOptionsLocalized()}
            />
            <DropdownRow
              {...page}
              marginTop
              label=" Channel"
              keyName={LocationVarKey.FMSplushasp7MoveOutMethod}
              options={[
                {
                  label: "Email",
                  value: "Email"
                },
                {
                  label: "SMS",
                  value: "SMS"
                },
                {
                  label: "Email & SMS",
                  value: "Both"
                }
              ]}
            />
            <Section {...page} hideIf={IS_EQUAL(LocationVarKey.FMSplushasp7MoveOutMethod, "Email")}>
              <TextAreaRow {...page} marginTop label=" SMS Message 1" keyName={LocationVarKey.FMSplusp7MoveOut1} />
              <TextAreaRow
                {...page}
                marginTop
                label=" SMS Message 2 (Optional)"
                keyName={LocationVarKey.FMSplusp7MoveOut2}
                enableSwitchVar={LocationVarKey.FMSplusp7MoveOut2Enabled}
              />
              <TextAreaRow
                {...page}
                marginTop
                label=" SMS Message 3 (Optional)"
                keyName={LocationVarKey.FMSplusp7MoveOut3}
                enableSwitchVar={LocationVarKey.FMSplusp7MoveOut3Enabled}
              />
            </Section>
            <SwitchRow {...page} keyName={LocationVarKey.FMSplushasp7MoveOutReview} label="Start Review Solicitation" />
          </Section>
        </>
      )}

      {sequenceArray && SequenceHelper.isAnySequenceSubType(SequenceSubType.MOVE_OUT_DAY_10, sequenceArray) && (
        <>
          <SectionHeader {...page}>10 days after move out</SectionHeader>
          <DropdownRow
            {...page}
            label="Enabled"
            marginTop
            keyName={LocationVarKey.SequenceMoveOutDay10Enabled}
            options={[
              { label: "Enabled", value: SequenceEnabledStatus.ENABLED },
              { label: "Disabled", value: SequenceEnabledStatus.DISABLED },
              { label: "Simulated", value: SequenceEnabledStatus.SIMULATED }
            ]}
          />
          <Section {...page} hideIf={IS_EQUAL(LocationVarKey.SequenceMoveOutDay10Enabled, SequenceEnabledStatus.DISABLED)}>
            <DropdownRow
              {...page}
              label="Time"
              marginTop
              keyName={LocationVarKey.SequenceMoveOutDay10Time}
              options={OptionsHelper.hoursOptionsLocalized()}
            />
            <DropdownRow
              {...page}
              marginTop
              label=" Channel"
              keyName={LocationVarKey.FMSplushasp10MoveOutMethod}
              options={[
                {
                  label: "Email",
                  value: "Email"
                },
                {
                  label: "SMS",
                  value: "SMS"
                },
                {
                  label: "Email & SMS",
                  value: "Both"
                }
              ]}
            />
            <Section {...page} hideIf={IS_EQUAL(LocationVarKey.FMSplushasp10MoveOutMethod, "Email")}>
              <TextAreaRow {...page} marginTop label=" SMS Message 1" keyName={LocationVarKey.FMSplusp10MoveOut1} />
              <TextAreaRow
                {...page}
                marginTop
                label=" SMS Message 2 (Optional)"
                keyName={LocationVarKey.FMSplusp10MoveOut2}
                enableSwitchVar={LocationVarKey.FMSplusp10MoveOut2Enabled}
              />
              <TextAreaRow
                {...page}
                marginTop
                label=" SMS Message 3 (Optional)"
                keyName={LocationVarKey.FMSplusp10MoveOut3}
                enableSwitchVar={LocationVarKey.FMSplusp10MoveOut3Enabled}
              />
            </Section>
            <SwitchRow {...page} keyName={LocationVarKey.FMSplushasp10MoveOutReview} label="Start Review Solicitation" />
          </Section>
        </>
      )}

      {sequenceArray && SequenceHelper.isAnySequenceSubType(SequenceSubType.MOVE_OUT_DAY_14, sequenceArray) && (
        <>
          <SectionHeader {...page}>14 days after move out</SectionHeader>
          <DropdownRow
            {...page}
            label="Enabled"
            marginTop
            keyName={LocationVarKey.SequenceMoveOutDay14Enabled}
            options={[
              { label: "Enabled", value: SequenceEnabledStatus.ENABLED },
              { label: "Disabled", value: SequenceEnabledStatus.DISABLED },
              { label: "Simulated", value: SequenceEnabledStatus.SIMULATED }
            ]}
          />
          <Section {...page} hideIf={IS_EQUAL(LocationVarKey.SequenceMoveOutDay14Enabled, SequenceEnabledStatus.DISABLED)}>
            <DropdownRow
              {...page}
              label="Time"
              marginTop
              keyName={LocationVarKey.SequenceMoveOutDay14Time}
              options={OptionsHelper.hoursOptionsLocalized()}
            />
            <DropdownRow
              {...page}
              marginTop
              label=" Channel"
              keyName={LocationVarKey.FMSplushasp14MoveOutMethod}
              options={[
                {
                  label: "Email",
                  value: "Email"
                },
                {
                  label: "SMS",
                  value: "SMS"
                },
                {
                  label: "Email & SMS",
                  value: "Both"
                }
              ]}
            />
            <Section {...page} hideIf={IS_EQUAL(LocationVarKey.FMSplushasp14MoveOutMethod, "Email")}>
              <TextAreaRow {...page} marginTop label=" SMS Message 1" keyName={LocationVarKey.FMSplusp14MoveOut1} />
              <TextAreaRow
                {...page}
                marginTop
                label=" SMS Message 2 (Optional)"
                keyName={LocationVarKey.FMSplusp14MoveOut2}
                enableSwitchVar={LocationVarKey.FMSplusp14MoveOut2Enabled}
              />
              <TextAreaRow
                {...page}
                marginTop
                label=" SMS Message 3 (Optional)"
                keyName={LocationVarKey.FMSplusp14MoveOut3}
                enableSwitchVar={LocationVarKey.FMSplusp14MoveOut3Enabled}
              />
            </Section>
            <SwitchRow {...page} keyName={LocationVarKey.FMSplushasp14MoveOutReview} label="Start Review Solicitation" />
          </Section>
        </>
      )}

      {sequenceArray && SequenceHelper.isAnySequenceSubType(SequenceSubType.MOVE_OUT_DAY_30, sequenceArray) && (
        <>
          <SectionHeader {...page}>30 days after move out</SectionHeader>
          <DropdownRow
            {...page}
            label="Enabled"
            marginTop
            keyName={LocationVarKey.SequenceMoveOutDay30Enabled}
            options={[
              { label: "Enabled", value: SequenceEnabledStatus.ENABLED },
              { label: "Disabled", value: SequenceEnabledStatus.DISABLED },
              { label: "Simulated", value: SequenceEnabledStatus.SIMULATED }
            ]}
          />
          <Section {...page} hideIf={IS_EQUAL(LocationVarKey.SequenceMoveOutDay30Enabled, SequenceEnabledStatus.DISABLED)}>
            <DropdownRow
              {...page}
              label="Time"
              marginTop
              keyName={LocationVarKey.SequenceMoveOutDay30Time}
              options={OptionsHelper.hoursOptionsLocalized()}
            />
            <DropdownRow
              {...page}
              marginTop
              label=" Channel"
              keyName={LocationVarKey.FMSplushasp30MoveOutMethod}
              options={[
                {
                  label: "Email",
                  value: "Email"
                },
                {
                  label: "SMS",
                  value: "SMS"
                },
                {
                  label: "Email & SMS",
                  value: "Both"
                }
              ]}
            />
            <Section {...page} hideIf={IS_EQUAL(LocationVarKey.FMSplushasp30MoveOutMethod, "Email")}>
              <TextAreaRow {...page} marginTop label=" SMS Message 1" keyName={LocationVarKey.FMSplusp30MoveOut1} />
              <TextAreaRow
                {...page}
                marginTop
                label=" SMS Message 2 (Optional)"
                keyName={LocationVarKey.FMSplusp30MoveOut2}
                enableSwitchVar={LocationVarKey.FMSplusp30MoveOut2Enabled}
              />
              <TextAreaRow
                {...page}
                marginTop
                label=" SMS Message 3 (Optional)"
                keyName={LocationVarKey.FMSplusp30MoveOut3}
                enableSwitchVar={LocationVarKey.FMSplusp30MoveOut3Enabled}
              />
            </Section>
            <SwitchRow {...page} keyName={LocationVarKey.FMSplushasp30MoveOutReview} label="Start Review Solicitation" />
          </Section>
        </>
      )}
    </>
  );
}
